import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FieldError } from "@/components/ui/field";
import { withStaffForm } from "@/hooks/staff/use-staff-form";
import { getStaffFormValues } from "@/default-form-values/staff/default-staff-form-values";
import { staffFormSchema } from "@/zod-schemas/staff/add-staff-schema";
import {
  DAY_OF_WEEK_LABEL,
  TIME_OPTIONS,
} from "@/constants/staff/staff-constants";
import { useIntl } from "react-intl";
import { isoTime } from "@/lib/utils";

interface StaffHoursStepProps {
  onNext: () => void;
  onBack: () => void;
}

export const StaffHoursStep = withStaffForm({
  defaultValues: getStaffFormValues(),
  validators: {
    onChange: staffFormSchema,
    onSubmit: staffFormSchema,
  },
  props: {} as StaffHoursStepProps,
  render: function StaffInfoStepRender(props) {
    const intl = useIntl();
    const { form, onNext, onBack } = props;

    const hours = form.state.values.hours;

    return (
      <div className="space-y-6">
        <div className="space-y-4">
          {hours.map((hour, index) => (
            <form.Field
              key={hour.day_of_week}
              name={`hours[${index}].is_active`}
              children={(activeField) => {
                const isActive = activeField.state.value;

                return (
                  <div className="grid grid-cols-[auto_1fr] items-center gap-4">
                    <div className="flex items-center gap-3 w-[140px]">
                      <Switch
                        checked={isActive}
                        onCheckedChange={(checked) =>
                          activeField.handleChange(checked)
                        }
                      />
                      <Label className="font-medium">
                        {DAY_OF_WEEK_LABEL[hour.day_of_week]}
                      </Label>
                    </div>

                    <div className="flex items-center gap-4">
                      <form.Field
                        name={`hours[${index}].start_time`}
                        children={(field) => (
                          <Select
                            value={field.state.value}
                            onValueChange={(value) => field.handleChange(value)}
                            disabled={!isActive}
                          >
                            <SelectTrigger className="w-[130px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {TIME_OPTIONS.map((time) => (
                                <SelectItem key={time} value={time}>
                                  {intl.formatTime(isoTime(time), { format: "clock" })}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />

                      <span className="text-sm text-muted-foreground">To</span>

                      <form.Field
                        name={`hours[${index}].end_time`}
                        children={(field) => (
                          <Select
                            value={field.state.value}
                            onValueChange={(value) => field.handleChange(value)}
                            disabled={!isActive}
                          >
                            <SelectTrigger className="w-[130px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {TIME_OPTIONS.map((time) => (
                                <SelectItem key={time} value={time}>
                                  {intl.formatTime(isoTime(time), { format: "clock" })}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  </div>
                );
              }}
            />
          ))}
        </div>

        <form.Field
          name="hours"
          children={(field) =>
            !field.state.meta.isValid ? (
              <FieldError errors={field.state.meta.errors} />
            ) : null
          }
        />

        <form.Subscribe
          selector={(state) =>
            state.values.hours.some((hour) => hour.is_active)
          }
          children={(hasActiveHours) => (
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onBack}>
                Back
              </Button>
              <Button type="button" onClick={onNext} disabled={!hasActiveHours}>
                Next
              </Button>
            </div>
          )}
        />
      </div>
    );
  },
});
